import React, {
  useState,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import Icon from '@components/Icon';
import clsx from 'clsx';

interface TooltipProps {
  message: ReactNode;
  className?: string;
}

const Tooltip = ({ message, className }: TooltipProps): ReactElement => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<
    'left' | 'right' | 'center'
  >('center');
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showTooltip && tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const clientWidth = document.documentElement.clientWidth;

      if (tooltipRect.right > clientWidth) {
        setTooltipPosition('left');
      } else if (tooltipRect.left < 0) {
        setTooltipPosition('right');
      } else {
        setTooltipPosition('center');
      }
    }
  }, [showTooltip]);

  return (
    <div
      className={clsx('relative', className)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onTouchStart={() => setShowTooltip(true)}
      onTouchEnd={() => setShowTooltip(false)}
    >
      <div
        id="tooltip"
        role="tooltip"
        ref={tooltipRef}
        className={clsx(
          'absolute bottom-full transform -translate-y-1 bg-dark rounded-large p-2 shadow-default w-[250px] text-smallSemibold text-[#D9D9D9]',
          {
            'left-1/2 -translate-x-1/2': tooltipPosition === 'center',
            'right-0': tooltipPosition === 'left',
            'left-0': tooltipPosition === 'right',
            hidden: !showTooltip,
          }
        )}
      >
        <div className="absolute bg-dark w-2 h-2 left-1/2 transform -translate-x-1/2 -bottom-1 rotate-45" />
        {message}
      </div>
      <Icon.Info className="w-[24px] h-[24px]" />
    </div>
  );
};

export default Tooltip;
